// src/stores/NotificationStore.js - Universal Notification Queue
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * Generate unique notification ID
 */
const generateId = () => `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * NotificationStore - Central queue for all application notifications
 *
 * Responsibilities:
 * - Maintain queue of active notifications
 * - Handle add/remove operations
 * - Auto-dismiss with timers
 * - Priority ordering
 */
const useNotificationStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE
    // ================================

    notifications: [], // Array of active notifications
    maxNotifications: 5, // Max visible at once
    paused: false, // Pause auto-dismiss (for user interaction)

    // ================================
    // ACTIONS
    // ================================

    /**
     * Add a notification to the queue
     * @param {object} notification - Notification data
     * @param {string} notification.type - 'success' | 'error' | 'warning' | 'info'
     * @param {string} notification.message - Main message text
     * @param {string} [notification.title] - Optional title
     * @param {number} [notification.duration] - Auto-dismiss duration (ms), null = manual dismiss
     * @param {string} [notification.icon] - Optional icon name
     * @param {array} [notification.actions] - Optional action buttons
     */
    add: (notification) => {
      const id = generateId();
      const timestamp = Date.now();

      const newNotification = {
        id,
        timestamp,
        type: notification.type || 'info',
        message: notification.message,
        title: notification.title,
        duration: notification.duration !== undefined ? notification.duration : (notification.type === 'error' ? null : 4000),
        icon: notification.icon,
        actions: notification.actions || [],
        priority: notification.priority || 'normal', // 'high' | 'normal' | 'low'
      };

      set(state => {
        // Add to queue
        const updated = [...state.notifications, newNotification];

        // Enforce max limit (remove oldest if exceeded)
        const limited = updated.length > state.maxNotifications
          ? updated.slice(updated.length - state.maxNotifications)
          : updated;

        return { notifications: limited };
      });

      // Set up auto-dismiss if duration specified
      if (newNotification.duration && !get().paused) {
        setTimeout(() => {
          get().remove(id);
        }, newNotification.duration);
      }

      console.log('🔔 Notification added:', newNotification);

      return id;
    },

    /**
     * Remove a notification by ID
     */
    remove: (id) => {
      set(state => ({
        notifications: state.notifications.filter(n => n.id !== id)
      }));
      console.log('🔔 Notification removed:', id);
    },

    /**
     * Clear all notifications
     */
    clear: () => {
      set({ notifications: [] });
      console.log('🔔 All notifications cleared');
    },

    /**
     * Pause auto-dismiss (e.g., when user hovers)
     */
    pause: () => {
      set({ paused: true });
    },

    /**
     * Resume auto-dismiss
     */
    resume: () => {
      set({ paused: false });
    },

    /**
     * Update notification (for progress updates, etc.)
     */
    update: (id, updates) => {
      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === id ? { ...n, ...updates } : n
        )
      }));
    },

    // ================================
    // SELECTORS
    // ================================

    /**
     * Get notifications sorted by priority and timestamp
     */
    getSorted: () => {
      const { notifications } = get();

      const priorityOrder = { high: 0, normal: 1, low: 2 };

      return [...notifications].sort((a, b) => {
        // First by priority
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;

        // Then by timestamp (newest first)
        return b.timestamp - a.timestamp;
      });
    },

    /**
     * Get count of active notifications
     */
    getCount: () => {
      return get().notifications.length;
    },

    /**
     * Check if queue is full
     */
    isFull: () => {
      return get().notifications.length >= get().maxNotifications;
    }
  }))
);

export default useNotificationStore;
